import {
  AdminLogoutResponse,
  AdminMeResponse,
  AdminUserConflictErrorResponse,
  AssetErrorResponse,
  CrmCreateConflictErrorResponse,
  CrmRevisionConflictEnvelope,
  CsrfRejectedErrorResponse,
  EditorialRevisionConflictEnvelope,
  ForbiddenErrorResponse,
  ReleaseConflictErrorResponse,
  RevisionConflictEnvelope,
  ServiceUnavailableErrorResponse,
  UnauthorizedErrorResponse,
  ValidationErrorResponse,
} from '@diana/contracts';

type ResponseSchema<T> = {
  parse(value: unknown): T;
};

type ApiErrorKind =
  'unauthorized' | 'forbidden' | 'conflict' | 'validation' | 'unavailable' | 'unexpected';

type RequestOptions<T> = {
  method?: 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
  notifyMutation?: boolean;
  schema: ResponseSchema<T>;
};

type AdminApiClientOptions = {
  fetcher?: typeof fetch;
};

export type AdminApiMutation = {
  path: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
};

export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly kind: ApiErrorKind,
    message = code,
    readonly fields?: Array<{ path: string; message: string }>,
    readonly payload?: unknown,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export function createAdminApiClient(options: AdminApiClientOptions = {}) {
  const fetcher = options.fetcher ?? fetch;
  let mePromise: Promise<ReturnType<typeof AdminMeResponse.parse>> | undefined;
  const mutationListeners = new Set<(mutation: AdminApiMutation) => void>();
  const sessionListeners = new Set<() => void>();
  let sessionLocked = false;
  let sessionGeneration = 0;
  let cachedMe: ReturnType<typeof AdminMeResponse.parse> | undefined;
  let freshPromise: Promise<ReturnType<typeof AdminMeResponse.parse>> | undefined;

  const execute = async <T>(
    path: string,
    request: RequestOptions<T>,
    allowCsrfRetry = true,
    anonymous = false,
  ): Promise<T> => {
    const generation = sessionGeneration;
    const target = sameOriginAdminPath(path);
    const method = request.method ?? 'GET';
    const headers = new Headers(request.headers);
    if (/^\/api\/v1\/admin\/sessions(?:[/?]|$)/.test(target))
      headers.set('accept', 'application/json; profile="gift-contacts"');
    if (
      /^\/api\/v1\/admin\/(?:sessions\/[^/]+\/gift-card(?:[/?]|$)|gift-card-preview(?:[/?]|$))/.test(
        target,
      )
    )
      headers.set('accept', 'application/json; profile="gift-card-editorial"');
    const unsafe = !['GET', 'HEAD'].includes(method);
    if (unsafe && !anonymous && sessionLocked && target !== '/api/v1/admin/auth/logout')
      throw new AdminApiError(
        401,
        'SESSION_REAUTH_REQUIRED',
        'unauthorized',
        'Sign in before saving',
      );
    if (request.body !== undefined) headers.set('content-type', 'application/json');
    let csrfSession: Promise<ReturnType<typeof AdminMeResponse.parse>> | undefined;
    if (unsafe && !anonymous) {
      csrfSession = getMe();
      const me = await csrfSession;
      headers.set('x-diana-csrf', me.csrfToken);
    }
    if (generation !== sessionGeneration)
      throw new AdminApiError(
        409,
        'SESSION_CHANGED',
        'unexpected',
        'Session changed before request',
      );
    if (unsafe && !anonymous && sessionLocked && target !== '/api/v1/admin/auth/logout')
      throw new AdminApiError(
        401,
        'SESSION_REAUTH_REQUIRED',
        'unauthorized',
        'Sign in before saving',
      );
    let response: Response;
    try {
      response = await fetcher(target, {
        method,
        credentials: 'same-origin',
        cache: target === '/api/v1/admin/me' ? 'no-store' : undefined,
        headers,
        body: request.body === undefined ? undefined : JSON.stringify(request.body),
        redirect: 'error',
        signal: request.signal,
      });
    } catch {
      throw new AdminApiError(0, 'NETWORK_ERROR', 'unavailable', 'Admin API is unavailable');
    }
    const payload = await responseJson(response);
    if (generation !== sessionGeneration)
      throw new AdminApiError(
        409,
        'SESSION_CHANGED',
        'unexpected',
        'Session changed during request',
      );
    if (!response.ok) {
      const error = normalizedError(response.status, payload);
      if (
        generation === sessionGeneration &&
        unsafe &&
        allowCsrfRetry &&
        error.code === 'CSRF_REJECTED'
      ) {
        if (mePromise === csrfSession) mePromise = undefined;
        return execute(path, request, false);
      }
      if (generation === sessionGeneration && response.status === 401 && !anonymous) {
        mePromise = undefined;
        cachedMe = undefined;
        sessionLocked = true;
        for (const listener of sessionListeners) listener();
      }
      throw error;
    }
    try {
      const parsed = request.schema.parse(payload);
      if (unsafe && !anonymous && method !== 'HEAD' && request.notifyMutation !== false) {
        const mutation = { path: target, method } as AdminApiMutation;
        for (const listener of mutationListeners) listener(mutation);
      }
      return parsed;
    } catch {
      throw new AdminApiError(
        response.status,
        'INVALID_RESPONSE',
        'unexpected',
        'Admin API returned an invalid response',
      );
    }
  };

  const getMe = () => {
    if (!mePromise) {
      const generation = sessionGeneration;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const request = execute('/api/v1/admin/me', {
        method: 'GET',
        schema: AdminMeResponse,
        signal: controller.signal,
      })
        .then((me) => {
          if (generation === sessionGeneration) cachedMe = me;
          return me;
        })
        .finally(() => clearTimeout(timeout));
      mePromise = request;
      void request.catch(() => {
        if (mePromise === request) mePromise = undefined;
      });
    }
    return mePromise;
  };
  const refreshSession = () => {
    if (freshPromise) return freshPromise;
    mePromise = undefined;
    const request = getMe();
    freshPromise = request;
    void request
      .finally(() => {
        if (freshPromise === request) freshPromise = undefined;
      })
      .catch(() => {});
    return request;
  };

  const login = async (email: string, password: string) => {
    cachedMe = undefined;
    ++sessionGeneration;
    mePromise = undefined;
    freshPromise = undefined;
    const response = await execute(
      '/api/v1/admin/auth/login',
      {
        method: 'POST',
        body: { email, password },
        schema: AdminMeResponse,
      },
      false,
      true,
    );
    freshPromise = undefined;
    cachedMe = response;
    mePromise = Promise.resolve(response);
    return response;
  };

  return {
    getMe,
    refreshSession,
    adoptSession(me: ReturnType<typeof AdminMeResponse.parse>) {
      ++sessionGeneration;
      freshPromise = undefined;
      cachedMe = me;
      mePromise = Promise.resolve(me);
    },
    setSessionLocked(locked: boolean) {
      sessionLocked = locked;
    },
    subscribeToSessionExpiry(listener: () => void) {
      sessionListeners.add(listener);
      return () => {
        sessionListeners.delete(listener);
      };
    },
    login,
    async logout() {
      ++sessionGeneration;
      freshPromise = undefined;
      mePromise = cachedMe ? Promise.resolve(cachedMe) : undefined;
      const response = await execute('/api/v1/admin/auth/logout', {
        method: 'POST',
        schema: AdminLogoutResponse,
      });
      mePromise = undefined;
      return response;
    },
    clearSession() {
      cachedMe = undefined;
      ++sessionGeneration;
      mePromise = undefined;
      freshPromise = undefined;
    },
    subscribeToMutations(listener: (mutation: AdminApiMutation) => void) {
      mutationListeners.add(listener);
      return () => {
        mutationListeners.delete(listener);
      };
    },
    get<T>(path: string, schema: ResponseSchema<T>) {
      return execute(path, { method: 'GET', schema });
    },
    request<T>(path: string, request: RequestOptions<T>) {
      return execute(path, request);
    },
  };
}

export type AdminApiClient = ReturnType<typeof createAdminApiClient>;

function sameOriginAdminPath(path: string) {
  if (!path.startsWith('/') || path.includes('\\') || path.includes('#')) {
    throw new Error('Expected a same-origin admin API path');
  }
  const url = new URL(path, 'https://admin.invalid');
  if (
    url.origin !== 'https://admin.invalid' ||
    (url.pathname !== '/api/v1/admin' && !url.pathname.startsWith('/api/v1/admin/')) ||
    url.pathname.includes('//') ||
    url.pathname.includes('%') ||
    url.pathname.split('/').some((segment) => segment === '.' || segment === '..')
  ) {
    throw new Error('Expected a same-origin admin API path');
  }
  return `${url.pathname}${url.search}`;
}

async function responseJson(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function normalizedError(status: number, payload: unknown) {
  const kind = errorKind(status);
  const parsed = sharedError(status, payload);
  const error = errorRecord(parsed);
  return new AdminApiError(
    status,
    typeof error?.code === 'string' ? error.code : fallbackCode(status),
    kind,
    typeof error?.message === 'string' ? error.message : fallbackMessage(kind),
    fieldErrors(error?.fields),
    parsed ?? undefined,
  );
}

function sharedError(status: number, payload: unknown) {
  const schemas =
    status === 401
      ? [UnauthorizedErrorResponse]
      : status === 403
        ? [ForbiddenErrorResponse, CsrfRejectedErrorResponse]
        : status === 409
          ? [
              RevisionConflictEnvelope,
              EditorialRevisionConflictEnvelope,
              CrmRevisionConflictEnvelope,
              CrmCreateConflictErrorResponse,
              AdminUserConflictErrorResponse,
              ReleaseConflictErrorResponse,
              AssetErrorResponse,
            ]
          : status === 422
            ? [ValidationErrorResponse, AssetErrorResponse]
            : status === 503
              ? [ServiceUnavailableErrorResponse, AssetErrorResponse]
              : [];
  for (const schema of schemas) {
    const result = schema.safeParse(payload);
    if (result.success) return result.data;
  }
  return null;
}

function errorRecord(payload: unknown) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const error = (payload as { error?: unknown }).error;
  return error && typeof error === 'object' && !Array.isArray(error)
    ? (error as Record<string, unknown>)
    : null;
}

function fieldErrors(value: unknown) {
  if (!Array.isArray(value)) return undefined;
  const fields = value.filter(
    (item): item is { path: string; message: string } =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as { path?: unknown }).path === 'string' &&
      typeof (item as { message?: unknown }).message === 'string',
  );
  return fields.length === value.length ? fields : undefined;
}

function errorKind(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation';
  if (status === 503) return 'unavailable';
  return 'unexpected';
}

function fallbackCode(status: number) {
  return (
    {
      401: 'UNAUTHORIZED',
      403: 'FORBIDDEN',
      409: 'CONFLICT',
      422: 'VALIDATION_FAILED',
      503: 'SERVICE_UNAVAILABLE',
    }[status] ?? 'UNEXPECTED_RESPONSE'
  );
}

function fallbackMessage(kind: ApiErrorKind) {
  return kind === 'unavailable' ? 'Admin API is unavailable' : 'Admin API request failed';
}
